import { useCallback, useEffect, useState } from 'react';
import {
  promotionopsApi,
  type CampaignRecord,
  type EventRecord,
  type ForecastRecord,
  type PerformanceRecord,
  type PromotionOfferRecord,
  type PromotionOpsDashboard,
  type PromotionRecommendation,
  type ScenarioRecord,
} from '../../api/promotionops';

type Tab = 'dashboard' | 'campaigns' | 'promotions' | 'events' | 'forecasts' | 'scenarios' | 'performance' | 'recommendations';

function statusClass(s: string) {
  if (s === 'active' || s === 'approved' || s === 'ready' || s === 'completed') return 'text-emerald-700 bg-emerald-50';
  if (s === 'scheduled' || s === 'planning' || s === 'draft' || s === 'upcoming') return 'text-amber-700 bg-amber-50';
  if (s === 'cancelled' || s === 'paused' || s === 'stale') return 'text-red-700 bg-red-50';
  return 'text-slate-600 bg-slate-50';
}

function promoTypeLabel(t?: string) {
  return t?.replace(/_/g, ' ') ?? '';
}

function fmtCurrency(n: number) {
  return n >= 1000 ? `$${(n / 1000).toFixed(1)}K` : `$${n.toFixed(0)}`;
}

export function PromotionOpsPage() {
  const [tab, setTab] = useState<Tab>('dashboard');
  const [dashboard, setDashboard] = useState<PromotionOpsDashboard | null>(null);
  const [campaigns, setCampaigns] = useState<CampaignRecord[]>([]);
  const [promotions, setPromotions] = useState<PromotionOfferRecord[]>([]);
  const [events, setEvents] = useState<EventRecord[]>([]);
  const [forecasts, setForecasts] = useState<ForecastRecord[]>([]);
  const [scenarios, setScenarios] = useState<ScenarioRecord[]>([]);
  const [performance, setPerformance] = useState<PerformanceRecord[]>([]);
  const [recommendations, setRecommendations] = useState<PromotionRecommendation[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, camp, prom, evt, fcst, scn, perf, recs] = await Promise.all([
        promotionopsApi.dashboard(), promotionopsApi.listCampaigns(), promotionopsApi.listPromotions(),
        promotionopsApi.listEvents(), promotionopsApi.listForecasts(), promotionopsApi.listScenarios(),
        promotionopsApi.listPerformance(), promotionopsApi.listRecommendations(),
      ]);
      setDashboard(dash); setCampaigns(camp); setPromotions(prom); setEvents(evt);
      setForecasts(fcst); setScenarios(scn); setPerformance(perf); setRecommendations(recs);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load promotion operations data');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const generateRecs = async () => {
    setBusy(true);
    try { await promotionopsApi.generateRecommendations(); await refresh(); } finally { setBusy(false); }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'dashboard', label: 'Commercial Dashboard' },
    { id: 'campaigns', label: 'Campaign Planner' },
    { id: 'promotions', label: 'Promotion Workspace' },
    { id: 'events', label: 'Event Calendar' },
    { id: 'forecasts', label: 'Forecasts' },
    { id: 'scenarios', label: 'Scenario Simulator' },
    { id: 'performance', label: 'Performance' },
    { id: 'recommendations', label: 'AI Recommendations' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Promotions &amp; Commercial Campaigns</h1>
          <p className="text-sm text-slate-500">Deals, coupons, events, forecasting &amp; AI commercial copilot — APP-00007</p>
        </div>
        <button type="button" onClick={refresh} disabled={busy}
          className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50">Refresh</button>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${tab === t.id ? 'bg-sky-100 text-sky-700' : 'text-slate-600 hover:bg-slate-100'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'dashboard' && dashboard && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { label: 'Active Campaigns', value: dashboard.active_campaigns.toString() },
            { label: 'Scheduled', value: dashboard.scheduled_campaigns.toString() },
            { label: 'Upcoming Events', value: dashboard.upcoming_events.toString() },
            { label: 'Total Budget', value: fmtCurrency(dashboard.total_budget) },
            { label: 'Budget Utilization', value: `${dashboard.budget_utilization_pct.toFixed(0)}%` },
            { label: 'Avg ROI', value: `${dashboard.avg_roi.toFixed(1)}x` },
            { label: 'Event Readiness', value: `${dashboard.avg_event_readiness_pct.toFixed(0)}%` },
            { label: 'AI Recommendations', value: dashboard.active_recommendations.toString() },
          ].map((kpi) => (
            <div key={kpi.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{kpi.label}</p>
              <p className="mt-1 text-2xl font-semibold text-slate-900">{kpi.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'campaigns' && (
        <div className="space-y-3">
          {campaigns.map((c) => (
            <div key={c.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-slate-900">{c.name}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(c.status)}`}>{c.status}</span>
                {c.promotion_type && <span className="rounded-full bg-violet-50 px-2 py-0.5 text-xs text-violet-700">{promoTypeLabel(c.promotion_type)}</span>}
                {c.requires_approval && <span className="text-xs text-amber-600">Requires approval</span>}
              </div>
              <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-600">
                <span>Budget: {fmtCurrency(c.budget)}</span>
                <span>Spent: {fmtCurrency(c.spent)}</span>
                <span>{c.product_count} products</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'promotions' && (
        <div className="space-y-3">
          {promotions.map((p) => (
            <div key={p.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                {p.sku && <span className="font-mono text-sm">{p.sku}</span>}
                <span className="rounded-full bg-violet-50 px-2 py-0.5 text-xs text-violet-700">{promoTypeLabel(p.promotion_type)}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(p.status)}`}>{p.status}</span>
                {p.discount_pct != null && p.discount_pct > 0 && <span className="text-sm font-medium text-emerald-700">{p.discount_pct}% off</span>}
              </div>
              {p.eligibility && <p className="mt-1 text-xs text-slate-400">{p.eligibility.replace(/_/g, ' ')}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'events' && (
        <div className="space-y-3">
          {events.map((e) => (
            <div key={e.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-slate-900">{e.name}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(e.status)}`}>{e.status}</span>
                {e.marketplace && <span className="text-xs text-slate-400">{e.marketplace}</span>}
              </div>
              <div className="mt-2 flex gap-4 text-sm text-slate-600">
                {e.days_until > 0 && <span>{e.days_until} days away</span>}
                <span>Readiness: {e.readiness_pct.toFixed(0)}%</span>
              </div>
              <div className="mt-2 h-2 rounded-full bg-slate-100">
                <div className="h-2 rounded-full bg-sky-500" style={{ width: `${e.readiness_pct}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'forecasts' && (
        <div className="space-y-3">
          {forecasts.map((f) => (
            <div key={f.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(f.status)}`}>{f.status}</span>
                <span className="text-xs text-slate-400">Risk: {(f.risk_score * 100).toFixed(0)}%</span>
              </div>
              <div className="mt-2 grid gap-2 sm:grid-cols-3 text-sm text-slate-600">
                <span>Demand: {f.expected_demand.toLocaleString()} units</span>
                <span>Revenue: {fmtCurrency(f.expected_revenue)}</span>
                <span>Profit: {fmtCurrency(f.expected_profit)}</span>
                <span>Inventory: {f.required_inventory.toLocaleString()}</span>
                <span>Ad Budget: {fmtCurrency(f.advertising_budget)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'scenarios' && (
        <div className="space-y-3">
          {scenarios.map((s) => (
            <div key={s.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="font-medium text-slate-900">{s.name}</h3>
              {s.description && <p className="mt-1 text-sm text-slate-600">{s.description}</p>}
              <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-600">
                <span>Revenue: {fmtCurrency(s.projected_revenue)}</span>
                <span>Profit: {fmtCurrency(s.projected_profit)}</span>
                <span className="text-emerald-700">ROI: {s.projected_roi.toFixed(1)}x</span>
                <span>Margin: {s.margin_impact_pct.toFixed(1)}%</span>
                <span>Break-even: {s.break_even_days} days</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'performance' && (
        <div className="space-y-3">
          {performance.map((p) => (
            <div key={p.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{p.campaign_name}</span>
                <span className="text-lg font-semibold text-emerald-700">{p.roi.toFixed(1)}x ROI</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-600">
                <span>Revenue: {fmtCurrency(p.incremental_revenue)}</span>
                <span>Profit: {fmtCurrency(p.incremental_profit)}</span>
                <span>{p.units_sold} units</span>
                <span>Conv: {(p.conversion_rate * 100).toFixed(1)}%</span>
                <span>Margin: {p.margin_impact_pct.toFixed(1)}%</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'recommendations' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button type="button" onClick={generateRecs} disabled={busy}
              className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50">
              Generate AI Recommendations
            </button>
          </div>
          {recommendations.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <h3 className="font-medium text-slate-900">{r.title}</h3>
                  {r.description && <p className="mt-1 text-sm text-slate-600">{r.description}</p>}
                </div>
                <span className="rounded-full bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700">#{r.priority_rank}</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-500">
                <span>{r.type.replace(/_/g, ' ')}</span>
                <span>Confidence: {(r.confidence_score * 100).toFixed(0)}%</span>
                {r.expected_impact && <span>Impact: {r.expected_impact}</span>}
                {r.requires_approval && <span className="text-amber-600">Requires approval</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
